import { createFileRoute } from '@tanstack/react-router';
import { AppHeader } from '@/components/layout/AppHeader';
import { Shield, EyeOff, Trash2, Lock, CheckCircle2, XCircle } from 'lucide-react';

export const Route = createFileRoute('/extension/privacy')({
  component: ExtensionPrivacyPage,
});

function ExtensionPrivacyPage() {
  return (
    <div className="h-screen overflow-y-auto studio-canvas">
      <AppHeader />

      <div className="mx-auto w-full max-w-3xl px-6 md:px-10 py-12">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold text-primary mb-4">
            <Shield className="h-3.5 w-3.5" />
            Privacy Policy
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight studio-text-primary">
            4K Labs Capture · Extension Privacy
          </h1>
          <p className="text-sm studio-text-secondary mt-3">
            Effective April 19, 2026 · Last updated April 19, 2026
          </p>
        </div>

        <div className="space-y-10 text-[15px] leading-relaxed studio-text-primary">
          <Section title="What this covers">
            <p>
              This policy describes the data captured by the 4K Labs Capture
              Chrome extension (the "Extension"). It supplements the{' '}
              <a href="https://4klabs.ai/privacy" className="text-primary underline underline-offset-2">
                main 4K Labs privacy policy
              </a>
              {' '}which governs our broader service.
            </p>
          </Section>

          <Section title="How recording works">
            <p>
              You start a recording from the Extension popup. The Extension then
              records a structured representation of the page you are on — DOM
              events, click targets, element coordinates, and periodic
              screenshots — so 4K Labs can reconstruct your workflow as a
              branded, narrated video.
            </p>
            <p className="mt-3">
              Recording only runs when you click <strong>Start Recording</strong>.
              While recording is active, a "4K Labs · Recording" badge is pinned
              to the top-right of the page so you always see that we're
              capturing. Clicking <strong>Stop</strong> ends the recording
              immediately.
            </p>
          </Section>

          <Section title="What we capture">
            <ul className="space-y-2.5 ml-4 list-disc marker:text-primary">
              <li>DOM structure and changes (via rrweb)</li>
              <li>Mouse position, clicks, scrolls, hover events</li>
              <li>Element-level metadata (tag, text, role, position)</li>
              <li>Screenshots at meaningful moments (page load, click settle, scroll)</li>
              <li>Page title, URL, and viewport size</li>
            </ul>
          </Section>

          <Section title="What we do NOT capture">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Typed input values are masked by default.</p>
                  <p className="text-sm studio-text-secondary mt-1">
                    If you type a password, card number, email, address, or any
                    other form value, we record <em>that</em> you typed —
                    never <em>what</em> you typed.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Sensitive iframes are never recorded.</p>
                  <p className="text-sm studio-text-secondary mt-1">
                    Payment processors (Stripe, Checkout.com, Braintree, PayPal,
                    Adyen, Klarna, Afterpay, Affirm), banking/ACH (Plaid),
                    identity providers (Auth0, Google/Microsoft/Apple sign-in,
                    Okta, OneLogin), and captcha widgets are completely skipped
                    by our recorder.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Screenshots of sensitive iframes are redacted.</p>
                  <p className="text-sm studio-text-secondary mt-1">
                    Even though Chrome's native tab capture renders all pixels,
                    we detect payment/auth iframe regions client-side and paint
                    them over with a solid "REDACTED" block before the
                    screenshot ever leaves your browser.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Audio, microphone, and webcam.</p>
                  <p className="text-sm studio-text-secondary mt-1">
                    Not captured, ever. The Extension has no audio or camera
                    permissions.
                  </p>
                </div>
              </div>
            </div>
          </Section>

          <Section title="Where data goes">
            <p>
              Recordings are stored in the Extension's local IndexedDB on your
              own machine until you click <strong>Upload to 4K Labs</strong>.
              Only then is the recording uploaded to our servers
              (<code className="text-xs px-1.5 py-0.5 rounded bg-muted font-mono">api-aws.4klabs.ai</code>)
              over HTTPS and associated with your 4K Labs account.
            </p>
            <p className="mt-3">
              Once uploaded, recordings are stored in Amazon S3 with access
              limited to the uploading user. Thumbnails are signed on demand
              and expire after one hour.
            </p>
          </Section>

          <Section title="Retention and deletion">
            <div className="flex items-start gap-3">
              <Trash2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p>
                  You can delete any capture at any time from{' '}
                  <a href="/captures" className="text-primary underline underline-offset-2">
                    /captures
                  </a>
                  . Deleted captures are removed from S3 and the database within
                  24 hours. Raw recording JSON is archived for up to 30 days for
                  troubleshooting and then permanently destroyed.
                </p>
              </div>
            </div>
          </Section>

          <Section title="Authentication">
            <p>
              The Extension authenticates via OAuth through your existing 4K
              Labs login. The Extension never sees or stores your password.
              Instead, we mint a scoped access token (a "Personal Access
              Token") limited to the extension's needs; you can revoke it at
              any time from your 4K Labs profile.
            </p>
          </Section>

          <Section title="Permissions the Extension requests">
            <div className="space-y-3 text-sm">
              <PermRow name="activeTab + scripting">To inject the recorder into the tab you explicitly start recording on.</PermRow>
              <PermRow name="tabs, downloads">To show recording state and export the recording as a downloadable JSON file if you choose.</PermRow>
              <PermRow name="storage">To remember your connected 4K Labs account and cached recordings on your machine.</PermRow>
              <PermRow name="cookies, identity">To complete the OAuth handoff with 4K Labs so you don't paste access tokens by hand.</PermRow>
              <PermRow name="host_permissions">To let the recorder run on any site you want to capture. We never send data to any site other than 4K Labs.</PermRow>
            </div>
          </Section>

          <Section title="Changes to this policy">
            <p>
              If we materially change what the Extension captures, we'll update
              this page and note the change in the Extension's release notes.
              For the broader 4K Labs service policy, see{' '}
              <a href="https://4klabs.ai/privacy" className="text-primary underline underline-offset-2">
                4klabs.ai/privacy
              </a>.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Questions or data requests:{' '}
              <a href="mailto:privacy@4klabs.ai" className="text-primary underline underline-offset-2">
                privacy@4klabs.ai
              </a>
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-lg font-semibold tracking-tight studio-text-primary mb-3 flex items-center gap-2">
        <Lock className="h-4 w-4 text-primary/70" />
        {title}
      </h2>
      <div className="pl-6 border-l-2 border-primary/10">{children}</div>
    </section>
  );
}

function PermRow({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <EyeOff className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
      <div>
        <code className="text-xs font-mono px-1.5 py-0.5 rounded bg-muted">{name}</code>
        <span className="studio-text-secondary ml-2">{children}</span>
      </div>
    </div>
  );
}
